import { customAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const regionStep = {
  title: "Configure the AWS region (optional)",
  items: [
    {
      kind: "markdown",
      content:
        "By default, the region and service will be automatically detected from request URLs (e.g., `s3.us-west-2.amazonaws.com`).\n\nOnly specify a region here if you need to override the URL-based detection or if your URLs don’t include region information.",
    },
    {
      kind: "input",
      label: "Region (optional)",
      placeholder: "us-east-1",
      helperText:
        "Leave blank to auto-detect from URLs, or specify a default region.",
      target: { kind: "configuration", field: "region" },
    },
  ],
} as const;

const connectionTest = {
  protocol: "http",
  request: () =>
    "https://sts.amazonaws.com/?Action=GetCallerIdentity&Version=2011-06-15",
} as const;

export const aws = defineProduct({
  name: "AWS",
  tagLine: "Cloud infrastructure and services",
  logo,
  tags: ["featured", "it", "engineering"],
  variants: [
    productVariant({
      identifier: "aws",
      authLabel: "Key-based (access/secret key pair)",
      protocol: "http",
      defaultUrlPattern: () => "https://.*\\.amazonaws\\.com(\\.cn|\\.gov)?/.*",
      auth: (app) =>
        customAuth("awsSigV4", {
          accessKeyId: app.config.accessKeyId,
          secretAccessKey: app.secret.secretAccessKey,
          region: app.config.region,
        }),
      steps: [
        {
          title: "Create an IAM user with programmatic access",
          items: [
            {
              kind: "markdown",
              content:
                "Visit the [IAM Console](https://console.aws.amazon.com/iam/home#/users) and create a new user with programmatic access, or use an existing IAM user.\n\nEnsure the user has the necessary permissions for the AWS services you want to use in 3B.",
            },
          ],
        },
        {
          title: "Generate access keys",
          items: [
            {
              kind: "markdown",
              content:
                "In the IAM user’s **Security credentials** tab, create a new access key. Choose **Application running outside AWS** as the use case.\n\nCopy the **Access key ID** and **Secret access key** – you’ll need them in the next step.",
            },
          ],
        },
        {
          title: "Enter your AWS credentials",
          items: [
            {
              kind: "input",
              label: "Access key ID",
              placeholder: "AKIA...",
              required: true,
              target: { kind: "configuration", field: "accessKeyId" },
            },
            {
              kind: "input",
              label: "Secret access key",
              placeholder: "Paste your secret access key",
              required: true,
              target: { kind: "secrets", field: "secretAccessKey" },
            },
          ],
        },
        regionStep,
      ],
      connectionTest,
      publicEnvVars: ({ region }) => {
        const trimmedRegion = region?.trim() ?? "";
        return {
          AWS_ACCESS_KEY_ID: "{will-be-injected}",
          AWS_SECRET_ACCESS_KEY: "{will-be-injected}",
          AWS_REGION: trimmedRegion || "us-east-1",
        };
      },
    }),
    productVariant({
      identifier: "awsRole",
      authLabel: "OIDC (IAM role federation)",
      protocol: "http",
      defaultUrlPattern: () => "https://.*\\.amazonaws\\.com/.*",
      auth: customAuth("awsRole"),
      steps: [
        {
          title: "Set up an OIDC trust",
          items: [
            {
              kind: "markdown",
              content:
                "Register 3B as an OpenID Connect identity provider in your AWS account:\n\n1. Open the [IAM identity providers page](https://console.aws.amazon.com/iam/home#/identity_providers)\n2. Choose **Add provider** → **OpenID Connect**\n3. For **Provider URL**, enter the URL shown below\n4. For **Audience**, enter the value shown below\n5. Click **Add provider**",
            },
            {
              kind: "copyable",
              label: "Provider URL",
              value: "{OIDC_PROVIDER_URL}",
            },
            {
              kind: "copyable",
              label: "Audience",
              value: "connected-app:{CONNECTED_APP_ID}",
            },
          ],
        },
        {
          title: "Create an IAM role",
          items: [
            {
              kind: "markdown",
              content:
                "Create a role that trusts the 3B OIDC provider:\n\n1. Open the [IAM roles page](https://console.aws.amazon.com/iam/home#/roles) and click **Create role**\n2. Choose **Web identity**, select the 3B provider you just added, and select the audience shown above\n3. Attach the permissions your workflows need\n4. Name the role and create it\n5. Copy the **Role ARN** from the role summary",
            },
          ],
        },
        {
          title: "Enter the role details",
          items: [
            {
              kind: "input",
              label: "Role ARN",
              placeholder: "arn:aws:iam::123456789012:role/My3BRole",
              required: true,
              target: { kind: "configuration", field: "roleArn" },
            },
          ],
        },
        regionStep,
      ],
      connectionTest,
      usageHint: () =>
        "The AWS_* env var values are placeholder strings, not real credentials — leave them in place so the AWS SDK and aws CLI pick them up and attempt to sign, but don’t branch on or print them. The proxy intercepts every outbound HTTPS call to *.amazonaws.com, strips the local signature, and re-signs with short-lived AssumeRoleWithWebIdentity credentials. Use the SDK or CLI normally; call sts:GetCallerIdentity if you need the account ID.",
      publicEnvVars: ({ region }) => {
        const trimmedRegion = region?.trim() ?? "";
        return {
          AWS_ACCESS_KEY_ID: "{will-be-injected}",
          AWS_SECRET_ACCESS_KEY: "{will-be-injected}",
          AWS_SESSION_TOKEN: "{will-be-injected}",
          AWS_REGION: trimmedRegion || "us-east-1",
        };
      },
    }),
  ],
});
